import { inject } from '@angular/core';
import { StorageService } from './services/public-api';
import { StorageKey } from './enums/public-api';

/**
 * Ensures paging defaults. Accepts any object with optional pageNumber/pageSize.
 */
export function ensureQueryDefaults<T extends { pageNumber?: number; pageSize?: number }>(
  query: T,
  defaultPageSize: number
): Required<Pick<T, 'pageNumber' | 'pageSize'>> & T {
  return {
    ...query,
    pageNumber: Math.max(1, query.pageNumber ?? 1),
    pageSize: Math.max(1, query.pageSize ?? defaultPageSize),
  } as Required<Pick<T, 'pageNumber' | 'pageSize'>> & T;
}

/**
 * Shallow merges entity fields while preserving referential equality if unchanged.
 */
export function mergeEntity<T extends Record<string, any>>(existing: T | undefined, incoming: T): T {
  if (!existing) return { ...incoming } as T;
  let changed = false;
  const merged: Record<string, any> = { ...existing };
  for (const [k, v] of Object.entries(incoming)) {
    if (v === undefined) continue;
    if (merged[k] !== v) {
      merged[k] = v;
      changed = true;
    }
  }
  return (changed ? merged : existing) as T;
}

/**
 * Converts configured date fields (if present) to UTC ISO strings.
 */
export function convertDatesToUTC<T extends Record<string, any>>(data: T, dateFields: string[]): T {
  if (!data) return data;
  const result: Record<string, any> = { ...data };
  for (const f of dateFields) {
    const value = result[f];
    if (!value) continue;
    try {
      result[f] = new Date(value).toISOString();
    } catch {
      // swallow conversion errors; retain original value
    }
  }
  return result as T;
}

/**
 * Backend `DateOnly` fields reject a full ISO datetime payload. Strips the
 * time/offset component using local date parts (not toISOString(), which
 * would shift the date backward for zones ahead of UTC near midnight).
 */
export function convertDatesToDateOnly<T extends Record<string, any>>(data: T, dateFields: string[]): T {
  if (!data) return data;
  const result: Record<string, any> = { ...data };
  for (const f of dateFields) {
    const value = result[f];
    if (!value) continue;
    try {
      const date = value instanceof Date ? value : new Date(value);
      if (isNaN(date.getTime())) continue;
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      result[f] = `${year}-${month}-${day}`;
    } catch {
      // swallow conversion errors; retain original value
    }
  }
  return result as T;
}

/**
 * Helper to retrieve tenant id in store contexts; throws if unavailable.
 * Can be used standalone or via DI if called inside a method where inject() is valid.
 */
export function getTenantIdOrThrow(storage: StorageService): string {
  const id = storage.getItem<{ id: string }>(StorageKey.tenant)?.id;
  if (!id) throw new Error('Tenant id is not available');
  return id;
}

/**
 * Convenience for inline usage within signalStore withMethods: const getTenantId = injectGetTenantId();
 */
export function injectGetTenantId() {
  const storage = inject(StorageService);
  return () => getTenantIdOrThrow(storage);
}

/**
 * Helper to retrieve branch id in store contexts; throws if unavailable.
 */
export function getBranchIdOrThrow(storage: StorageService): string {
  const id = storage.getItem<{ id: string }>(StorageKey.branch)?.id;
  if (!id) throw new Error('Branch id is not available');
  return id;
}

/**
 * Convenience for inline usage within signalStore withMethods: const getBranchId = injectGetBranchId();
 */
export function injectGetBranchId() {
  const storage = inject(StorageService);
  return () => getBranchIdOrThrow(storage);
}

/**
 * Extracts the first API error message supporting both `errors` and legacy `errors`.
 */
export function extractFirstErrorMessage(response: any, fallback: string): string {
  return (
    response?.errors?.[0]?.message ||
    response?.errors?.[0]?.message ||
    fallback
  );
}
